/**
 * AgmuthDisplay: HUD widget showing the player's collected Agmuth items.
 *
 * Displays "AGMUTH:" label followed by up to 4 small inverted triangle
 * icons. Collected Agmuths are fully visible; uncollected slots are
 * dimmed outlines. When a new Agmuth is found, a dramatic glow + scale
 * effect plays on the new icon.
 */

import Phaser from 'phaser';
import { TEX_LOOT_AGMUTH, TEX_GLOW } from '../utils/constants.js';
import {
  AGMUTH_MAX_PER_GAME,
  AGMUTH_GHOST_GLOW_COLOR,
} from '../config/game-config.js';

export default class AgmuthDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene
   * @param {number} x - Left edge X position
   * @param {number} y - Center Y position
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** @type {number} Current count of collected Agmuths. */
    this._count = 0;

    /** @type {Phaser.GameObjects.Sprite[]} Agmuth icon sprites (always 4). */
    this._sprites = [];

    /** @type {boolean} Whether ghost-mode glow is active. */
    this._glowActive = false;

    /** @type {boolean} Whether the full-set (4/4) pulsing glow is active. */
    this._fullSetGlowActive = false;

    /** @type {Phaser.Tweens.Tween|null} Full-set pulsing glow tween. */
    this._fullSetGlowTween = null;

    /** @type {Phaser.GameObjects.Graphics|null} Full-set halo graphics. */
    this._fullSetHalo = null;

    /** @type {Phaser.GameObjects.Graphics|null} Gold halo behind the active icon. */
    this._glowHalo = null;

    /** @type {Phaser.Tweens.Tween|null} Pulsing glow tween. */
    this._glowTween = null;

    /** @type {Phaser.GameObjects.Particles.ParticleEmitter|null} Gold particles. */
    this._glowEmitter = null;

    // Label
    this._label = scene.add.text(x, y, 'AGMUTH:', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#888888',
    }).setOrigin(0, 0.5).setDepth(210);

    // Create all 4 icon slots (dimmed initially)
    this._createIcons();
  }

  /**
   * Set the Agmuth count (rebuilds icon states).
   *
   * @param {number} count - Number of Agmuths collected (0-4)
   */
  setCount(count) {
    this._count = Math.min(count, AGMUTH_MAX_PER_GAME);
    this._updateIcons();
  }

  /**
   * Increment the count and play a collect effect on the new icon.
   */
  addAgmuth() {
    if (this._count >= AGMUTH_MAX_PER_GAME) return;

    this._count += 1;
    this._updateIcons();

    // Play dramatic effect on the newly collected icon
    const idx = this._count - 1;
    const sprite = this._sprites[idx];
    if (sprite) {
      this._playCollectEffect(sprite);
    }
  }

  /**
   * Activate a pulsing golden glow on the last collected Agmuth icon.
   * Called when the player enters ghost mode (presses A).
   */
  activateGlow() {
    if (this._glowActive || this._count <= 0) return;
    this._glowActive = true;

    const idx = this._count - 1;
    const sprite = this._sprites[idx];
    if (!sprite || !sprite.active) return;

    // Gold tint on the icon sprite
    sprite.setTint(AGMUTH_GHOST_GLOW_COLOR);

    // Pulsing halo behind the icon
    this._glowHalo = this.scene.add.graphics();
    this._glowHalo.setDepth(209);
    this._drawGlowHalo(sprite, 0.35);

    // Pulsing animation: scale the halo alpha in/out
    this._glowTween = this.scene.tweens.add({
      targets: { val: 0.35 },
      val: 0.12,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (_tween, target) => {
        if (this._glowHalo && sprite && sprite.active) {
          this._drawGlowHalo(sprite, target.val);
        }
      },
    });

    // Subtle gold particle trail
    try {
      this._glowEmitter = this.scene.add.particles(sprite.x, sprite.y, TEX_GLOW, {
        lifespan: { min: 200, max: 450 },
        speed: { min: 5, max: 18 },
        scale: { start: 0.2, end: 0 },
        alpha: { start: 0.7, end: 0 },
        frequency: 80,
        quantity: 1,
        tint: AGMUTH_GHOST_GLOW_COLOR,
      });
      this._glowEmitter.setDepth(220);
    } catch (_err) {
      // Graceful fallback
    }
  }

  /**
   * Deactivate the golden glow effect on the Agmuth icon.
   * Called when ghost mode ends (after wall pass or cleanup).
   */
  deactivateGlow() {
    if (!this._glowActive) return;
    this._glowActive = false;

    // Remove tint from the icon
    const idx = this._count - 1;
    const sprite = this._sprites[idx];
    if (sprite && sprite.active) {
      sprite.clearTint();
    }

    // Destroy glow tween
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }

    // Destroy halo
    if (this._glowHalo) {
      this._glowHalo.destroy();
      this._glowHalo = null;
    }

    // Destroy particle emitter
    if (this._glowEmitter) {
      this._glowEmitter.stop();
      this.scene.time.delayedCall(500, () => {
        if (this._glowEmitter) {
          this._glowEmitter.destroy();
          this._glowEmitter = null;
        }
      });
    }
  }

  /**
   * Activate a constant pulsing golden glow on ALL 4 Agmuth icons.
   * Called when all 4 relics have been collected, signalling the
   * final battle is imminent. The glow persists until explicitly
   * deactivated or the display is destroyed.
   */
  activateFullSetGlow() {
    if (this._fullSetGlowActive) return;
    if (this._count < AGMUTH_MAX_PER_GAME) return;
    this._fullSetGlowActive = true;

    // Apply gold tint to all 4 icons
    for (const sprite of this._sprites) {
      if (sprite && sprite.active) {
        sprite.setTint(AGMUTH_GHOST_GLOW_COLOR);
      }
    }

    // Shared halo graphics drawn behind all 4 icons
    this._fullSetHalo = this.scene.add.graphics();
    this._fullSetHalo.setDepth(209);
    this._drawFullSetHalo(0.35);

    // Pulsing animation on the halo
    this._fullSetGlowTween = this.scene.tweens.add({
      targets: { val: 0.35 },
      val: 0.12,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (_tween, target) => {
        if (this._fullSetHalo) {
          this._drawFullSetHalo(target.val);
        }
      },
    });
  }

  /**
   * Deactivate the full-set golden glow on all icons.
   */
  deactivateFullSetGlow() {
    if (!this._fullSetGlowActive) return;
    this._fullSetGlowActive = false;

    // Clear tint from all icons
    for (const sprite of this._sprites) {
      if (sprite && sprite.active) {
        sprite.clearTint();
      }
    }

    if (this._fullSetGlowTween) {
      this._fullSetGlowTween.stop();
      this._fullSetGlowTween = null;
    }
    if (this._fullSetHalo) {
      this._fullSetHalo.destroy();
      this._fullSetHalo = null;
    }
  }

  /**
   * Consume an Agmuth with a smooth fade-shrink animation on the icon.
   * Called after the player passes through a wall.
   * Deactivates glow first, then animates the consumed icon away.
   *
   * @param {number} newCount - The new Agmuth count after consumption.
   */
  consumeAgmuth(newCount) {
    // Deactivate ghost-mode glow first (if still active)
    this.deactivateGlow();
    // Deactivate full-set glow (no longer 4/4 after consuming one)
    this.deactivateFullSetGlow();

    const consumedIdx = this._count - 1;
    const sprite = this._sprites[consumedIdx];

    // Update internal count
    this._count = Math.min(newCount, AGMUTH_MAX_PER_GAME);

    if (sprite && sprite.active && this.scene) {
      // Flash gold before fading away
      sprite.setTint(AGMUTH_GHOST_GLOW_COLOR);

      // Small burst of particles at the consumed icon
      try {
        const burst = this.scene.add.particles(sprite.x, sprite.y, TEX_GLOW, {
          lifespan: { min: 250, max: 500 },
          speed: { min: 12, max: 40 },
          scale: { start: 0.25, end: 0 },
          alpha: { start: 0.8, end: 0 },
          frequency: -1,
          quantity: 6,
          tint: AGMUTH_GHOST_GLOW_COLOR,
        });
        burst.setDepth(220);
        burst.explode(6);

        this.scene.time.delayedCall(600, () => {
          if (burst) burst.destroy();
        });
      } catch (_err) {
        // Graceful fallback
      }

      // Fade + shrink the consumed icon
      this.scene.tweens.add({
        targets: sprite,
        alpha: 0.2,
        scaleX: 0,
        scaleY: 0,
        duration: 400,
        ease: 'Back.easeIn',
        onComplete: () => {
          if (!sprite || !sprite.active) return;
          sprite.clearTint();
          // Reset to dimmed uncollected state
          sprite.setScale(0.8);
          sprite.setAlpha(0.2);
        },
      });
    } else {
      // No animation possible, just update icons
      this._updateIcons();
    }
  }

  /**
   * Destroy all display elements.
   */
  destroy() {
    // Clean up ghost-mode glow effects first
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glowHalo) {
      this._glowHalo.destroy();
      this._glowHalo = null;
    }
    if (this._glowEmitter) {
      this._glowEmitter.destroy();
      this._glowEmitter = null;
    }
    this._glowActive = false;

    // Clean up full-set glow effects
    if (this._fullSetGlowTween) {
      this._fullSetGlowTween.stop();
      this._fullSetGlowTween = null;
    }
    if (this._fullSetHalo) {
      this._fullSetHalo.destroy();
      this._fullSetHalo = null;
    }
    this._fullSetGlowActive = false;

    for (const sprite of this._sprites) {
      if (sprite) sprite.destroy();
    }
    this._sprites = [];
    if (this._label) {
      this._label.destroy();
      this._label = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Create all icon sprites for the max slots.
   * @private
   */
  _createIcons() {
    const startX = this.x + 52; // After "AGMUTH:" label
    const spacing = 14;

    for (let i = 0; i < AGMUTH_MAX_PER_GAME; i++) {
      const sprite = this.scene.add.sprite(
        startX + i * spacing,
        this.y,
        TEX_LOOT_AGMUTH,
      ).setOrigin(0.5).setDepth(210).setScale(0.8);

      // Start dimmed (uncollected)
      sprite.setAlpha(0.2);

      this._sprites.push(sprite);
    }
  }

  /**
   * Update icon alpha based on current count.
   * @private
   */
  _updateIcons() {
    for (let i = 0; i < this._sprites.length; i++) {
      const sprite = this._sprites[i];
      if (!sprite || !sprite.active) continue;

      if (i < this._count) {
        sprite.setAlpha(1);
      } else {
        sprite.setAlpha(0.2);
      }
    }
  }

  /**
   * Play a dramatic collect effect on a newly collected icon.
   * Glow burst + scale pop + subtle particles.
   * @private
   * @param {Phaser.GameObjects.Sprite} sprite
   */
  _playCollectEffect(sprite) {
    if (!sprite || !this.scene) return;

    // Start from zero scale, pop in dramatically
    sprite.setScale(0);
    sprite.setAlpha(1);

    // Phase 1: Scale up with overshoot
    this.scene.tweens.add({
      targets: sprite,
      scaleX: 1.4,
      scaleY: 1.4,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!sprite || !sprite.scene) return;

        // Phase 2: Settle to final scale
        this.scene.tweens.add({
          targets: sprite,
          scaleX: 0.8,
          scaleY: 0.8,
          duration: 200,
          ease: 'Quad.easeOut',
        });
      },
    });

    // Glow particles around the icon
    try {
      const emitter = this.scene.add.particles(sprite.x, sprite.y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 10, max: 35 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.9, end: 0 },
        frequency: -1,
        quantity: 8,
        tint: 0xffffff,
      });
      emitter.setDepth(220);
      emitter.explode(8);

      // Auto-destroy particles after they're done
      this.scene.time.delayedCall(700, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_err) {
      // Graceful fallback if particles fail
    }
  }

  /**
   * Draw a golden glow halo behind a sprite icon.
   * @private
   * @param {Phaser.GameObjects.Sprite} sprite - The icon sprite.
   * @param {number} alpha - The fill alpha for the halo.
   */
  _drawGlowHalo(sprite, alpha) {
    if (!this._glowHalo) return;
    this._glowHalo.clear();

    // Outer soft glow
    this._glowHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, alpha * 0.4);
    this._glowHalo.fillCircle(sprite.x, sprite.y, 14);

    // Inner brighter core
    this._glowHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, alpha);
    this._glowHalo.fillCircle(sprite.x, sprite.y, 8);
  }

  /**
   * Draw a golden glow halo behind ALL 4 Agmuth icon sprites.
   * @private
   * @param {number} alpha - The fill alpha for each halo.
   */
  _drawFullSetHalo(alpha) {
    if (!this._fullSetHalo) return;
    this._fullSetHalo.clear();

    for (const sprite of this._sprites) {
      if (!sprite || !sprite.active) continue;

      // Outer soft glow
      this._fullSetHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, alpha * 0.4);
      this._fullSetHalo.fillCircle(sprite.x, sprite.y, 14);

      // Inner brighter core
      this._fullSetHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, alpha);
      this._fullSetHalo.fillCircle(sprite.x, sprite.y, 8);
    }
  }
}
